import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Brain, Shield, AlertTriangle, Loader2 } from "lucide-react";
import { analyzeDocument, assessRisk } from "@/utils/aiUtils";

interface AnalysisResult {
  documentType: string;
  confidence: number;
  details: string[];
}

const FraudDetection = () => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);

  const handleDocumentAnalysis = async (file: File) => {
    setIsProcessing(true);
    try {
      const imageUrl = URL.createObjectURL(file);
      const results = await analyzeDocument(imageUrl);
      
      // Extract the top prediction and its details
      const topPrediction = results[0];
      
      const documentAnalysis: AnalysisResult = {
        documentType: topPrediction.label,
        confidence: topPrediction.score * 100,
        details: [
          `Document Classification: ${topPrediction.label}`,
          `Confidence Score: ${(topPrediction.score * 100).toFixed(2)}%`,
          `File Type: ${file.type}`,
          `File Size: ${(file.size / 1024).toFixed(2)} KB`,
        ]
      };

      setAnalysisResult(documentAnalysis);
      
      toast({
        title: "Document Analysis Complete",
        description: `Identified as ${documentAnalysis.documentType} with ${documentAnalysis.confidence.toFixed(2)}% confidence`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Analysis Failed",
        description: "There was an error analyzing the document.",
      });
      console.error("Document analysis error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTextAnalysis = async (text: string) => {
    setIsProcessing(true);
    try {
      const results = await assessRisk(text);
      const prediction = results[0];
      
      toast({
        title: "Text Analysis Complete",
        description: `Risk assessment: ${prediction.label} (${(prediction.score * 100).toFixed(2)}% confidence)`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Analysis Failed",
        description: "There was an error analyzing the text.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">AI-Powered Fraud Detection</h1>
        <Shield className="h-8 w-8 text-primary" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Document Verification</h2>
          <div className="space-y-4">
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleDocumentAnalysis(file);
              }}
              className="hidden"
              id="fraud-document-upload"
            />
            <label
              htmlFor="fraud-document-upload"
              className="block w-full p-4 border-2 border-dashed rounded-lg text-center cursor-pointer hover:border-primary"
            >
              Upload document for fraud detection
            </label>
            {isProcessing && (
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="animate-spin" />
                <span>Processing document...</span>
              </div>
            )}
            
            {analysisResult && !isProcessing && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Analysis Results</h3>
                <div className="space-y-2">
                  {analysisResult.details.map((detail, index) => (
                    <p key={index} className="text-sm text-gray-600">
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Text Analysis</h2>
          <div className="space-y-4">
            <Input
              placeholder="Enter text for analysis..."
              onChange={(e) => handleTextAnalysis(e.target.value)}
              disabled={isProcessing}
            />
            {isProcessing && (
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="animate-spin" />
                <span>Analyzing text...</span>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">AI Detection Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border rounded-lg">
            <Brain className="h-6 w-6 mb-2 text-primary" />
            <h3 className="font-semibold">Document Analysis</h3>
            <p className="text-sm text-gray-600">
              AI-powered analysis of uploaded documents for authenticity
            </p>
          </div>
          <div className="p-4 border rounded-lg">
            <AlertTriangle className="h-6 w-6 mb-2 text-primary" />
            <h3 className="font-semibold">Pattern Recognition</h3>
            <p className="text-sm text-gray-600">
              Detect suspicious patterns in user behavior and transactions
            </p>
          </div>
          <div className="p-4 border rounded-lg">
            <Shield className="h-6 w-6 mb-2 text-primary" />
            <h3 className="font-semibold">Risk Scoring</h3>
            <p className="text-sm text-gray-600">
              Automated risk assessment and scoring system
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FraudDetection;